import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnInit,
  ResourceSnapshot,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as pdfjsLib from 'pdfjs-dist';
import { RenderParameters } from 'pdfjs-dist/types/src/display/api';
import { asDataUri, blobToDataUri, DataUri, isResourceSnapshot } from '@softline/core';
import { IconComponent } from '../components/icon/icon.component';
import { TranslatePipe } from '../i18n/pipes/translate.pipe';

type PdfDocumentValue = Blob | DataUri | null | undefined;

const EMPTY_DOCUMENT_SNAPSHOT: ResourceSnapshot<PdfDocumentValue> = {
  status: 'resolved',
  value: null,
};

@Component({
  selector: 'soft-ds-pdf-viewer',
  imports: [CommonModule, IconComponent, TranslatePipe],
  templateUrl: './pdf-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerComponent implements OnInit, AfterViewInit {
  private viewInitialized = false;
  private pdf?: pdfjsLib.PDFDocumentProxy;

  readonly document = input.required<
    ResourceSnapshot<PdfDocumentValue> | PdfDocumentValue
  >();

  protected readonly documentSnapshot = computed<ResourceSnapshot<PdfDocumentValue>>(
    () => {
      const value = this.document();
      if (value == null) return EMPTY_DOCUMENT_SNAPSHOT;
      if (isResourceSnapshot<PdfDocumentValue>(value)) return value;
      return { status: 'resolved', value };
    }
  );

  protected readonly isLoading = computed(() => {
    const status = this.documentSnapshot().status;
    return status === 'loading' || status === 'reloading';
  });

  protected readonly resolvedDocument = computed(() => {
    const snapshot = this.documentSnapshot();
    return snapshot.status === 'error' ? null : (snapshot.value ?? null);
  });

  private readonly container = viewChild<ElementRef<HTMLCanvasElement>>('container');

  readonly currentPage = signal(1);
  readonly pages = signal(0);
  protected readonly renderError = signal(false);
  scale = 0.9;

  constructor() {
    effect(() => {
      const document = this.resolvedDocument();
      if (document && this.viewInitialized) void this.renderDocument(document);
    });
  }

  ngOnInit(): void {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/lib/pdf.js/build/pdf.worker.js';
  }

  async ngAfterViewInit(): Promise<void> {
    const document = this.resolvedDocument();
    if (document) await this.renderDocument(document);
    this.viewInitialized = true;
  }

  async renderDocument(document: Blob | DataUri): Promise<void> {
    this.renderError.set(false);
    try {
      const dataUrl =
        typeof document === 'string'
          ? asDataUri(document, { mediaTypes: ['application/pdf'] })
          : await blobToDataUri(document, { mediaTypes: ['application/pdf'] });
      this.pdf = await pdfjsLib.getDocument(dataUrl).promise;
      this.pages.set(this.pdf.numPages);
      await this.renderPage(1, this.scale);
    } catch {
      this.renderError.set(true);
    }
  }

  async nextPage(): Promise<void> {
    if (!this.pdf || this.currentPage() + 1 > this.pdf.numPages) return;
    await this.renderPage(this.currentPage() + 1, this.scale);
  }

  async prevPage(): Promise<void> {
    if (this.currentPage() <= 1) return;
    await this.renderPage(this.currentPage() - 1, this.scale);
  }

  async zoomIn(): Promise<void> {
    await this.renderPage(this.currentPage(), this.scale + 0.1);
  }

  async zoomOut(): Promise<void> {
    await this.renderPage(this.currentPage(), this.scale - 0.1);
  }

  async renderPage(no: number, scale: number): Promise<void> {
    this.currentPage.set(no);
    this.scale = scale;
    if (!this.pdf) return;
    const page = await this.pdf.getPage(no);
    const canvas = this.container()?.nativeElement;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;
    const viewport = page.getViewport({ scale });

    const outputScale = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = Math.floor(viewport.width) + 'px';
    canvas.style.height = Math.floor(viewport.height) + 'px';

    const transform =
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

    const renderContext: RenderParameters = {
      canvasContext: context,
      transform,
      viewport,
    };
    await page.render(renderContext).promise;
  }
}
